/**
 * Assessments API endpoints (backend `/bilans`).
 */

import { apiClient } from './api-client';
import type { PaginatedResponse } from '@/types/data-table.types';
import type {
  Assessment,
  AssessmentType,
  CreateAssessmentDto,
  PatchAssessmentPayloadDto,
  GetAllAssessmentsParams,
} from '@/types/assessment.types';

/** `GET /bilans` — paginated assessments. */
export const getAllAssessments = async (
  params?: GetAllAssessmentsParams,
): Promise<PaginatedResponse<Assessment>> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  if (params?.type) searchParams.append('type', params.type);
  if (params?.status) searchParams.append('status', params.status);
  if (params?.establishmentId) searchParams.append('establishmentId', params.establishmentId);
  if (params?.targetUserId) searchParams.append('targetUserId', params.targetUserId);
  if (params?.search) searchParams.append('search', params.search);
  const qs = searchParams.toString();
  const url = qs ? `/bilans?${qs}` : '/bilans';
  const response = await apiClient.get<PaginatedResponse<Assessment>>(url);
  return response.data;
};

/** `GET /bilans/:id` */
export const getAssessment = async (id: string): Promise<Assessment> => {
  const response = await apiClient.get<Assessment>(`/bilans/${id}`);
  return response.data;
};

/** Create empty `to_fill` assessment(s) — volunteer multi-target may return several. */
export const createAssessment = async (dto: CreateAssessmentDto): Promise<Assessment[]> => {
  const response = await apiClient.post<Assessment[]>('/bilans', dto);
  return response.data;
};

/** `PATCH /bilans/:id` */
export const patchAssessmentPayload = async (
  id: string,
  dto: PatchAssessmentPayloadDto,
): Promise<Assessment> => {
  const response = await apiClient.patch<Assessment>(`/bilans/${id}`, dto);
  return response.data;
};

/** `PATCH /bilans/:id/submit` */
export const submitAssessment = async (id: string): Promise<Assessment> => {
  const response = await apiClient.patch<Assessment>(`/bilans/${id}/submit`);
  return response.data;
};

/** `PATCH /bilans/:id/validate` */
export const validateAssessment = async (id: string): Promise<Assessment> => {
  const response = await apiClient.patch<Assessment>(`/bilans/${id}/validate`);
  return response.data;
};

/** `DELETE /bilans/:id` */
export const deleteAssessment = async (id: string): Promise<void> => {
  await apiClient.delete(`/bilans/${id}`);
};

/** Download the assessment export (PDF for volunteer/named/intermediate/other, Excel for self-assessment). */
export const exportAssessment = async (id: string, type: AssessmentType): Promise<void> => {
  const response = await apiClient.get(`/bilans/${id}/export`, { responseType: 'blob' });
  const contentType = (response.headers?.['content-type'] as string) || 'application/pdf';
  const extension = contentType.includes('spreadsheet') ? 'xlsx' : 'pdf';
  const blob = new Blob([response.data], { type: contentType });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `bilan-${type}-${id}.${extension}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};
